import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, setAuthToken } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Sparkles } from 'lucide-react';

export default function AuthCallback() {
  const navigate = useNavigate();
  const { loginWithToken, refreshUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const params = new URLSearchParams(hash.replace('#', '?'));

    // New first-party flow: token is in the URL hash from /api/auth/google/callback redirect
    const token = params.get('token');
    if (token) {
      setAuthToken(token);
      // Fetch user data using the token
      api.get('/me').then(res => {
        if (res.data.user) {
          loginWithToken(token, res.data.user);
        }
        window.history.replaceState(null, '', window.location.pathname);
        // Already on the right page (callback redirected to /dashboard or /admin/dashboard)
      }).catch(err => {
        console.error('Auth callback failed:', err);
        navigate('/auth');
      });
      return;
    }

    // Legacy Emergent auth flow: session_id in hash
    const sessionId = params.get('session_id');
    if (sessionId) {
      const processSession = async () => {
        try {
          const res = await api.post('/auth/google/session', { session_id: sessionId });
          if (res.data.token && res.data.user) {
            loginWithToken(res.data.token, res.data.user);
          }
          window.history.replaceState(null, '', window.location.pathname);
          navigate('/dashboard');
        } catch (err) {
          console.error('Google auth failed:', err);
          navigate('/auth');
        }
      };
      processSession();
      return;
    }

    // No token or session_id — try cookie-based session (first-party cookie set by callback)
    refreshUser().then(user => {
      if (user) {
        window.history.replaceState(null, '', window.location.pathname);
      } else {
        navigate('/auth');
      }
    });
  }, [navigate, loginWithToken, refreshUser]);

  return (
    <div className="min-h-screen bg-[#050B14] flex items-center justify-center">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4A843] to-[#F5D78E] flex items-center justify-center animate-pulse">
          <Sparkles className="w-5 h-5 text-[#0B1D3A]" />
        </div>
        <span className="font-['Outfit'] text-white/60 text-sm">Signing you in...</span>
      </div>
    </div>
  );
}
